import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { NotificationTypePreference } from '@/api/notificationPreferences/notificationPreferencesApi.types';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import {
  useNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from '@/hooks/useNotificationPreferences';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

export function NotificationPreferencesSection() {
  const { t } = useTranslation('notifications');
  const preferencesQuery = useNotificationPreferencesQuery();
  const updatePreferences = useUpdateNotificationPreferencesMutation();

  const preferences =
    preferencesQuery.data === undefined ? [] : preferencesQuery.data.preferences;
  const pendingUpdateItems =
    updatePreferences.isPending === true && updatePreferences.variables !== undefined
      ? updatePreferences.variables
      : [];
  const pendingUpdateItem = pendingUpdateItems.length > 0 ? pendingUpdateItems[0] : undefined;

  function toggleChannel(
    typePreference: NotificationTypePreference,
    channel: string,
    nextEnabled: boolean
  ) {
    updatePreferences.mutate(
      [
        {
          notificationType: typePreference.notificationType,
          channel,
          isEnabled: nextEnabled,
        },
      ],
      {
        onSuccess: function reportSaved() {
          showSuccessNotification(t('preferences.saved'));
        },
        onError: function reportError(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('preferences.saveError'));
        },
      }
    );
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Bell" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('preferences.title')}</h2>
      </div>
      <p className="text-sm text-zoomuted mb-5">{t('preferences.subtitle')}</p>

      {preferencesQuery.isLoading === true ? (
        <div className="py-8 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : preferences.length === 0 ? (
        <p className="text-sm text-zoomuted">{t('preferences.empty')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {preferences.map(function renderTypePreference(typePreference) {
            return (
              <div
                key={typePreference.notificationType}
                className="border border-zooborder rounded-xl p-4"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[13px] font-bold text-zooindigo">
                    {t('preferences.types.' + typePreference.notificationType)}
                  </span>
                  {typePreference.isMandatory === true ? (
                    <Badge tone="muted" size="sm" uppercase>
                      {t('preferences.required')}
                    </Badge>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {typePreference.channels.map(function renderChannel(channelPreference) {
                    const channelLabel = t('preferences.channels.' + channelPreference.channel);
                    const isThisChannelUpdating =
                      pendingUpdateItem !== undefined &&
                      pendingUpdateItem.notificationType === typePreference.notificationType &&
                      pendingUpdateItem.channel === channelPreference.channel;
                    return (
                      <div
                        key={channelPreference.channel}
                        className="flex items-center gap-2"
                      >
                        <ToggleSwitch
                          isChecked={channelPreference.isEnabled}
                          isDisabled={typePreference.isMandatory === true}
                          isPending={isThisChannelUpdating}
                          accessibleLabel={channelLabel}
                          onToggle={function toggle() {
                            toggleChannel(
                              typePreference,
                              channelPreference.channel,
                              channelPreference.isEnabled === false
                            );
                          }}
                        />
                        <span className="text-sm text-zoomuted">{channelLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
